import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
  ViewChild,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import { Router } from '@angular/router';
import { NgTemplateOutlet } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { animate, style, transition, trigger } from '@angular/animations';

import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { TranslateModule } from '@ngx-translate/core';

import { catchError, of } from 'rxjs';

import { ERoutes } from 'src/app/core/enums/routes.enum';
import { UserProfile } from 'src/app/core/interfaces/user-profile';
import { ITripRoute } from 'src/app/core/types/interfaces/popular-trip.interface';
import { IMapTripDestination } from 'src/app/core/types/interfaces/trip-elements.interface';

import { AuthService } from 'src/app/core/services/auth.service';
import { AiAssistantService } from 'src/app/core/services/ai-assistant.service';
import { TripWizardStateService } from '../../trip-wizard/trip-wizard-state.service';
import { GoogleAnalyticService } from 'src/app/core/services/google-analytic.service';

import { Trip } from '../../../classes/trip';
import { PreviewMapComponent } from '../../../maps/preview-map/preview-map.component';
import { AiChatInputMobileComponent } from '../ai-chat-input-mobile/ai-chat-input-mobile.component';
import {
  IChatCity,
  IChatMessage,
} from '../../ai-chat-dialog/ai-chat-dialog.component';
import { CustomizeThisTripDialogComponent } from '../../../dialogs/customize-this-trip-dialog/customize-this-trip-dialog.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-ai-chat-mobile',
  templateUrl: './ai-chat-mobile.component.html',
  styleUrls: ['./ai-chat-mobile.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    PreviewMapComponent,
    NgxSkeletonLoaderModule,
    NgTemplateOutlet,
  ],
  animations: [
    trigger('questionInAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(25px)' }),
        animate(
          '400ms 50ms ease-out',
          style({ opacity: 1, transform: 'translateY(0)' }),
        ),
      ]),
    ]),
    trigger('answerInAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(25px)' }),
        animate(
          '400ms 450ms ease-out',
          style({ opacity: 1, transform: 'translateY(0)' }),
        ),
      ]),
    ]),
  ],
})
export class AiChatMobileComponent {
  @Input() aiChatInputMobileComponent: AiChatInputMobileComponent;
  @Output() backToPlannerOverview: EventEmitter<void> = new EventEmitter();

  @ViewChild('messagesList') messagesListContainer: ElementRef;

  messages: IChatMessage[] = [];
  aiAssistant: { messages: IChatMessage[]; routes: IChatCity[] } = {
    messages: [],
    routes: [],
  };

  destinations: any[] = [];
  destinationsRoute: IMapTripDestination[] = [];
  preferences: string[] = [];
  userProfile: UserProfile | null = null;
  isFetchingMap: boolean = false;
  isFetchingAnswer: boolean = true;
  countryCoordinates: [number, number] = [12.4942486, 41.8905198];

  readonly examples: string[] = [
    'Plan a 12 day trip from Rome to Milan',
    'Plan a romantic winery trip to Tuscany',
  ];

  private readonly tripState: WritableSignal<Trip>;

  #tripWizardStateService: TripWizardStateService = inject(
    TripWizardStateService,
  );

  #matDialog: MatDialog = inject(MatDialog);

  constructor(
    protected googleAnalyticService: GoogleAnalyticService,
    private authService: AuthService,
    private aiAssistantService: AiAssistantService,
    private router: Router,
  ) {
    this.userProfile = this.authService.userProfile;
    this.tripState = signal(this.#tripWizardStateService.currentTripStateValue);
  }

  sendMessage(): void {
    if (!this.messages.length) {
      this.isFetchingMap = true;
    }
    this.googleAnalyticService.setUseAiPlan({
      userID: this.authService.userId,
    });

    const messageText =
      this.aiChatInputMobileComponent.inputRef.nativeElement.value;
    const newMessage: IChatMessage = {
      question: messageText,
      answer: '',
      cities: [],
      preferences: [],
      typewriting: true,
    };
    this.messages.push(newMessage);
    this.aiChatInputMobileComponent.reset();

    this.isFetchingAnswer = true;
    this.autoScrollToBottom(50);
    this.aiAssistantService
      .sendMessage(messageText, this.preferences)
      .pipe(catchError((err) => of(err)))
      .subscribe((res) => {
        if (res && res.answer) {
          const answer = res.answer;
          let autoscrollCount = Math.floor(answer.length / 10);
          newMessage.cities = res.cities;
          newMessage.preferences = res.preferences;
          this.messages.splice(this.messages.length - 1, 1, newMessage);

          for (let i = 0; i <= answer.length; i++) {
            setTimeout(() => {
              newMessage.answer = answer.substring(0, i);

              if (i >= autoscrollCount) {
                autoscrollCount += answer.length / 10;
                this.autoScrollToBottom();
              }

              if (i === answer.length) {
                newMessage.typewriting = false;
                this.isFetchingAnswer = false;
                this.isFetchingMap = false;
                this.aiAssistant.messages = this.messages;

                if (newMessage.cities.length) {
                  this.aiAssistant.messages = this.messages;

                  if (newMessage.cities.length === 1) {
                    newMessage.cities.push(...newMessage.cities);
                  }

                  this.aiAssistantService
                    .createAiRoute(newMessage.cities, this.aiAssistant)
                    .subscribe((destinations: any[]) => {
                      this.destinations = destinations;
                      this.getRouteCoordinates(destinations);
                    });
                }
              }
            }, i * 10);
          }
        } else {
          newMessage.answer = 'Oops, something went wrong, please try again..';
          newMessage.typewriting = false;
          this.messages.splice(this.messages.length - 1, 1, newMessage);
        }
      });
  }

  private autoScrollToBottom(delay = 150): void {
    setTimeout(() => {
      const messagesContainer = this.messagesListContainer.nativeElement;
      if (messagesContainer) {
        messagesContainer.scrollTo({
          top: messagesContainer.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, delay);
  }

  private getRouteCoordinates(route: any): void {
    if (!route) return;
    this.destinationsRoute = [];

    route.forEach((r: any) => {
      const parsedCityCoordinate = r.coordinates
        .split(',')
        .map((cs: string) => parseFloat(cs.trim()))
        .reverse();
      this.destinationsRoute.push({
        coordinates: parsedCityCoordinate,
        nextLegIsFlight: r.nextLegIsFlight,
      });
    });
  }

  submitCustomizeAiTrip(): void {
    const dialog = this.#matDialog.open(CustomizeThisTripDialogComponent);

    dialog.afterClosed().subscribe((isConfirm) => {
      if (!isConfirm) return;

      this.#tripWizardStateService.onNewCreateTrip$.next();

      let duration = 0;
      const chatCities = this.aiAssistant.routes;
      const firstDestination = chatCities[0];
      const lastDestination = chatCities[chatCities.length - 1];
      const routes: ITripRoute[] = [];

      chatCities.forEach((r) => {
        duration += r.days_in_city;
        routes.push({
          coordinates: '0, 0',
          countryId: r.country_id,
          days: r.days_in_city,
          id: r.place_id,
          name: r.name,
        });
      });

      this.tripState.update((trip) =>
        trip.updateTrip({
          name: '',
          savesId: '',
          blogUrl: '',
          isOptimizedMode: false,
          startingLocationId: String(firstDestination.place_id),
          startingLabel: String(firstDestination.name),
          startingCountry: String(firstDestination.country_id),
          endingLocationId: String(lastDestination.place_id),
          endingLabel: String(lastDestination.name),
          endingCountry: String(lastDestination.country_id),
          numDays: Number(duration),
          route: routes,
          plannerInfo: {
            type: 'ai-optimizer',
            name: '',
            description: '',
          },
          includes: [],
          excludes: [],
        }),
      );

      this.#tripWizardStateService.currentTripState = new Trip({
        ...this.tripState(),
      });
      this.#tripWizardStateService.byUserInteraction = true;

      setTimeout(() => {
        this.router
          .navigate([ERoutes.SLASH, ERoutes.TRIP_PLANNER])
          .then(() => this.backToPlannerOverview.emit());
      }, 100);
    });
  }
}
